import * as THREE from 'three';

import { Axial, axialKey, axialToWorld } from '../map/HexCoordinates';
import { HEX_SIZE, elevationToY } from '../map/HexMetrics';
import { deckY } from '../map/RoadSurface';
import { HexCellData } from '../map/HexSurface';
import { crossingSeconds } from '../map/Movement';

/**
 * Wozy na mapie.
 *
 * **Bliźniak `PlayerMarkers` i to jest cała jego treść**: pionek stoi na polu,
 * pole przychodzi z serwera, a ruch między polami wygładza się po tej stronie,
 * żeby nie skakał. Różni się trzema rzeczami i wszystkie trzy są o tym, czym
 * wóz jest: nie ma podpisu z nazwą, nie ma koloru właściciela i jedzie
 * **wolniej**, bo tak jedzie na serwerze.
 *
 * Nie jest osobną machinerią, bo nie ma czym być: gdyby kiedyś jechały tędy
 * karawany albo wojsko, to jest ten sam plik z inną bryłą.
 */

/** Skrzynia na kołach: niska i szeroka, żeby z izometrii nie udawała pionka. */
const WIDTH = HEX_SIZE * 0.42;
const HEIGHT = HEX_SIZE * 0.3;

/**
 * Najkrótsze przejście między polami.
 *
 * Podłoga na wypadek terenu, o którym klient jeszcze nic nie wie: krok liczony
 * na zero trwałby zero i pionek by skoczył.
 */
const SHORTEST_STEP = 0.05;

interface Marker {
  cell: Axial;
  mesh: THREE.Mesh;
  target: THREE.Vector3;
  /** Skąd zaczęło się to przejście. */
  from: THREE.Vector3;
  /** Ile sekund trwa — dokładnie tyle, ile na serwerze. */
  duration: number;
  elapsed: number;
  /**
   * Wóz dojechał i ma zniknąć **po dojechaniu**, nie w tej chwili.
   *
   * Serwer wysyła ostatni `convoyMoved` i `convoyGone` w tym samym takcie: dla
   * niego wóz jest już na miejscu i rozładowany. Dla patrzącego nie — ostatnie
   * przejście dopiero się zaczęło, więc skasowanie mesha od razu gasiło wóz
   * o **pole przed celem**, jakby rozpłynął się w drodze.
   */
  leaving: boolean;
}

export class ConvoyMarkers {
  readonly object = new THREE.Group();

  private readonly markers = new Map<string, Marker>();
  private readonly geometry = new THREE.BoxGeometry(WIDTH, HEIGHT, WIDTH * 0.7);
  private readonly material = new THREE.MeshStandardMaterial({
    color: 0x9a7b4f,
    roughness: 0.85,
    flatShading: true,
  });

  /**
   * Ile wolniej jedzie wóz wobec kroku człowieka. Bliźniak `haulage.pace`.
   *
   * Podawany z katalogu, gdy ten dojedzie — a nie wpisany tutaj — bo to jest ta
   * sama liczba, którą serwer dzieli czas kroku. Do tego czasu wozów i tak nie
   * ma czym rysować.
   */
  private pace = 1;

  constructor(private readonly world: Map<number, HexCellData>) {
    this.object.name = 'convoys';
  }

  setPace(pace: number): void {
    this.pace = pace > 0 ? pace : 1;
  }

  private groundY(cell: Axial): number | null {
    const data = this.world.get(axialKey(cell.q, cell.r));
    if (!data) return null;
    // Wóz na moście jedzie po pokładzie, jak człowiek.
    return deckY(data) ?? elevationToY(data.elevation);
  }

  private place(cell: Axial, fallback: number): THREE.Vector3 {
    const { x, z } = axialToWorld(cell.q, cell.r);
    const y = this.groundY(cell) ?? fallback;
    return new THREE.Vector3(x, y + HEIGHT / 2, z);
  }

  set(id: string, cell: Axial): void {
    const held = this.markers.get(id);
    if (held) {
      held.cell = cell;
      // Ruch wozu, który miał odjechać, znaczy, że jednak jedzie dalej.
      held.leaving = false;
      held.target = this.place(cell, held.mesh.position.y - HEIGHT / 2);

      // Czas kroku, nie prędkość: ta sama funkcja, którą liczy go serwer,
      // podzielona przez tempo wozu. Stała prędkość dojeżdżała na miejsce przed
      // czasem i czekała — i to jest ten ruch skokowy.
      held.from.copy(held.mesh.position);
      held.duration = Math.max(
        SHORTEST_STEP,
        crossingSeconds(this.world.get(axialKey(cell.q, cell.r))) / this.pace
      );
      held.elapsed = 0;
      return;
    }

    const mesh = new THREE.Mesh(this.geometry, this.material);
    mesh.castShadow = true;
    const at = this.place(cell, 0);
    mesh.position.copy(at);
    this.object.add(mesh);

    this.markers.set(id, {
      cell,
      mesh,
      target: at,
      from: at.clone(),
      duration: SHORTEST_STEP,
      elapsed: SHORTEST_STEP,
      leaving: false,
    });
  }

  /**
   * Który wóz stoi na tym polu.
   *
   * Bliźniak `PlayerMarkers.whoIsAt` i jest tu z tego samego powodu: kliknięcie
   * zna pole, a nie wóz. Pole z markera, nie z pozycji mesha — ten jest
   * w połowie drogi między dwoma polami przez większość czasu.
   */
  whoIsAt(cell: Axial): string | null {
    for (const [id, marker] of this.markers) {
      // Wóz w drodze do zniknięcia jest już rozładowany i serwer o nim nie wie:
      // zajrzeć do niego nie sposób, więc nie ma go tu po co pokazywać.
      if (marker.leaving) continue;
      if (marker.cell.q === cell.q && marker.cell.r === cell.r) return id;
    }
    return null;
  }

  /**
   * Wóz dojechał: niech **dojedzie** i dopiero wtedy zniknie.
   *
   * Zniknięcie jest u serwera chwilą — rozładował i skasował wóz w tym samym
   * takcie, w którym postawił go na ostatnim polu. Tutaj jest końcem ruchu,
   * więc marker zostaje do końca przejścia i gaśnie w `update`. Gdy nic się nie
   * dzieje, gaśnie natychmiast, bo nie ma czego dokańczać.
   */
  remove(id: string): void {
    const marker = this.markers.get(id);
    if (!marker) return;

    if (marker.elapsed < marker.duration) {
      marker.leaving = true;
      return;
    }

    this.drop(id, marker);
  }

  private drop(id: string, marker: Marker): void {
    this.object.remove(marker.mesh);
    this.markers.delete(id);
  }

  /**
   * Zdejmij wszystkie, i to od razu.
   *
   * Nie przez `remove`: sprzątanie po zmianie świata albo rozłączeniu nie jest
   * dojazdem do celu i nie ma na co czekać — wozy, których dokańczanie drogi
   * dotyczy, i tak przestały istnieć.
   */
  clear(): void {
    for (const [id, marker] of [...this.markers]) this.drop(id, marker);
  }

  /** Teren, który właśnie dojechał, to teren, nad którym wóz mógł wisieć. */
  refresh(): void {
    for (const marker of this.markers.values()) {
      const ground = this.groundY(marker.cell);
      if (ground === null) continue;
      // Oba końce przejścia, nie tylko cel — inaczej wóz wjeżdżałby na miejsce
      // od dołu, przez ziemię.
      marker.target.setY(ground + HEIGHT / 2);
      marker.from.setY(ground + HEIGHT / 2);
    }
  }

  update(delta: number): void {
    for (const [id, marker] of [...this.markers]) {
      if (marker.elapsed >= marker.duration) {
        marker.mesh.position.copy(marker.target);
        // Dojechał i miał zniknąć — teraz jest na to pora.
        if (marker.leaving) this.drop(id, marker);
        continue;
      }

      // Liniowo w czasie, licząc od punktu wyjścia: ułamek drogi to ułamek
      // czasu. Interpolacja od bieżącej pozycji byłaby ruchem zwalniającym przy
      // każdym polu, bo za każdym razem brałaby ułamek tego, co zostało.
      marker.elapsed += delta;
      const part = Math.min(1, marker.elapsed / marker.duration);
      marker.mesh.position.copy(marker.from).lerp(marker.target, part);
    }
  }

  dispose(): void {
    this.clear();
    this.geometry.dispose();
    this.material.dispose();
    this.object.removeFromParent();
  }
}
